/**
 * Describes the name and purpose of a flag.
 *
 * @property name - The name of the flag.
 * @property description - A description of what the flag means.
 */
export interface Flag {
  readonly name: string
  readonly label: string
}

/**
 * Describes the name and purpose of a flag, as well as the bitwise code to match that flag.
 *
 * @property name - The name of the flag.
 * @property description - A description of what the flag means.
 * @property code - The bitwise code used to represent the flag.
 */
export interface MatchFlag {
  readonly name: string
  readonly label: string
  readonly code: number
}

/**
 * Takes a bit-code and a set bit-flags and determines which flags were expressed in the bit-code.
 * For one example, look at https://xrpl.org/transaction-common-fields.html#flags-field.
 *
 * @param num - The bitwise code the possible flags are matched against.
 * @param flags - The possible flags that might be expressed in the code.
 * @returns An array of flags that were expressed within the code.
 */
export function matchFlags(
  num: number | undefined,
  flags: MatchFlag[],
): Flag[] {
  if (!num) {
    return []
  }

  return (
    flags
      // eslint-disable-next-line no-bitwise -- Need bitwise operation to match flags.
      .filter((value: MatchFlag) => value.code & num)
      .map(
        (value): Flag => {
          return {
            name: value.name,
            label: value.label,
          }
        },
      )
  )
}
